import { NavLink } from 'react-router-dom'
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  LogOut,
  Target,
  User,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { activeModules } from './navigation-config'
import type { ShellModule, ShellUserSummary } from './types'

interface ShellSidebarProps {
  isMobile: boolean
  mobileMenuOpen: boolean
  collapsed: boolean
  logoHorizontal: string
  logoIcon: string
  pathname: string
  user: ShellUserSummary | null
  expandedModules: string[]
  onNavigate: (href: string) => void
  onToggleModule: (moduleId: string) => void
  onExpandSidebar: () => void
  onCollapseSidebar: () => void
  onLogout: () => void
}

function isPathActive(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`)
}

function isPathExact(pathname: string, href: string): boolean {
  return pathname === href
}

export function ShellSidebar({
  isMobile,
  mobileMenuOpen,
  collapsed,
  logoHorizontal,
  logoIcon,
  pathname,
  user,
  expandedModules,
  onNavigate,
  onToggleModule,
  onExpandSidebar,
  onCollapseSidebar,
  onLogout,
}: ShellSidebarProps) {
  const sidebarWidth = collapsed ? 'w-16' : 'w-72'

  const handleModuleClick = (module: ShellModule) => {
    if (module.comingSoon) {
      return
    }
    onNavigate(module.href)
  }

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-50 flex flex-col border-r border-border bg-card/95 backdrop-blur-xl transition-all duration-300 ease-in-out',
        isMobile
          ? cn('w-72 transform', mobileMenuOpen ? 'translate-x-0' : '-translate-x-full')
          : sidebarWidth
      )}
    >
      <div
        className={cn(
          'flex items-center border-b border-border transition-all duration-300',
          collapsed && !isMobile ? 'h-14 justify-center px-2' : 'h-14 justify-between px-4'
        )}
      >
        {(!collapsed || isMobile) && (
          <div className="flex items-center min-w-0 flex-1">
            <img
              src={logoHorizontal}
              alt="AMPERSAND"
              className="h-8 max-w-[200px] object-contain"
            />
          </div>
        )}

        {collapsed && !isMobile && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={onExpandSidebar}
                className="w-10 h-10 rounded-lg hover:bg-muted/50 flex items-center justify-center transition-all duration-200 group"
                aria-label="Expand sidebar"
              >
                <img
                  src={logoIcon}
                  alt="AMPERSAND"
                  className="h-8 w-8 object-contain opacity-80 group-hover:opacity-100 transition-opacity"
                />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="font-medium">
              Expand sidebar
            </TooltipContent>
          </Tooltip>
        )}

        {!collapsed && !isMobile && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onCollapseSidebar}
                className="h-8 w-8 flex-shrink-0 text-muted-foreground hover:text-foreground"
                aria-label="Collapse sidebar"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              Collapse sidebar
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-2">
        {(!collapsed || isMobile) && (
          <div className="px-3 mb-2">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-2">
              Platform
            </p>
          </div>
        )}

        <div className="space-y-1 px-2">
          {collapsed && !isMobile ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <NavLink
                  to="/dashboard"
                  className={cn(
                    'flex items-center justify-center rounded-lg p-2.5 transition-all',
                    isPathActive(pathname, '/dashboard')
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <LayoutDashboard className="h-5 w-5" />
                </NavLink>
              </TooltipTrigger>
              <TooltipContent side="right">SPECTREIQ Dashboard</TooltipContent>
            </Tooltip>
          ) : (
            <NavLink
              to="/dashboard"
              className={cn(
                'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all',
                isPathActive(pathname, '/dashboard')
                  ? 'bg-primary/10 text-primary shadow-[inset_0_0_0_1px_rgba(56,189,248,0.2)]'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <LayoutDashboard className="h-5 w-5 flex-shrink-0" />
              <span>SPECTREIQ Dashboard</span>
            </NavLink>
          )}

          {collapsed && !isMobile ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <NavLink
                  to="/missions"
                  className={cn(
                    'flex items-center justify-center rounded-lg p-2.5 transition-all',
                    isPathActive(pathname, '/missions')
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <Target className="h-5 w-5" />
                </NavLink>
              </TooltipTrigger>
              <TooltipContent side="right">Missions</TooltipContent>
            </Tooltip>
          ) : (
            <NavLink
              to="/missions"
              className={cn(
                'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all',
                isPathActive(pathname, '/missions')
                  ? 'bg-primary/10 text-primary shadow-[inset_0_0_0_1px_rgba(56,189,248,0.2)]'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <Target className="h-5 w-5 flex-shrink-0" />
              <span>Missions</span>
            </NavLink>
          )}
        </div>

        {(!collapsed || isMobile) && (
          <div className="px-3 mt-6 mb-2">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-2">
              Modules
            </p>
          </div>
        )}

        {collapsed && !isMobile && <div className="my-4 mx-2 border-t border-border" />}

        <div className="space-y-1 px-2">
          {activeModules.map((module) => {
            const isModuleActive = isPathActive(pathname, module.href)
            const isModuleExpanded = expandedModules.includes(module.id)
            const ModuleIcon = module.icon

            if (collapsed && !isMobile) {
              return (
                <Tooltip key={module.id}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => handleModuleClick(module)}
                      className={cn(
                        'w-full flex items-center justify-center rounded-lg p-2.5 transition-all',
                        isModuleActive
                          ? 'bg-primary/10 text-primary'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      )}
                    >
                      <ModuleIcon className="h-5 w-5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {module.name}
                  </TooltipContent>
                </Tooltip>
              )
            }

            return (
              <div key={module.id}>
                <button
                  onClick={() => handleModuleClick(module)}
                  className={cn(
                    'w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all',
                    isPathExact(pathname, module.href)
                      ? 'bg-primary/10 text-primary shadow-[inset_0_0_0_1px_rgba(56,189,248,0.2)]'
                      : isModuleActive
                        ? 'text-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <ModuleIcon className="h-5 w-5 flex-shrink-0" />
                  <span className="flex-1 text-left">{module.name}</span>
                  {module.items.length > 0 && (
                    <span
                      onClick={(event) => {
                        event.stopPropagation()
                        onToggleModule(module.id)
                      }}
                      className="p-1 hover:bg-muted rounded"
                    >
                      {isModuleExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </span>
                  )}
                </button>

                {/* Flat item list (2-level nav) */}
                {isModuleExpanded && module.items.length > 0 && (
                  <div className="ml-4 mt-1 space-y-0.5 border-l border-border/50 pl-3">
                    {module.items.map((item) => {
                      const ItemIcon = item.icon
                      return (
                        <div key={item.href}>
                          {item.separator && (
                            <div className="my-1.5 border-t border-border/30" />
                          )}
                          <NavLink
                            to={item.href}
                            className={cn(
                              'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-all',
                              isPathActive(pathname, item.href)
                                ? 'bg-primary/10 text-primary'
                                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                            )}
                          >
                            <ItemIcon className="h-3.5 w-3.5" />
                            <span>{item.name}</span>
                          </NavLink>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </nav>

      <div className="border-t border-border p-2">
        <div
          className={cn(
            'flex items-center gap-3 rounded-lg px-3 py-2',
            collapsed && !isMobile ? 'justify-center px-2' : ''
          )}
        >
          {collapsed && !isMobile ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center cursor-default">
                  <User className="w-4 h-4 text-white" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" className="font-medium">
                <p>{user?.firstName || user?.username || 'User'}</p>
                <p className="text-xs text-muted-foreground">{user?.roles?.[0] || 'analyst'}</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <>
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {user?.firstName || user?.username || 'User'}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {user?.roles?.[0] || 'analyst'}
                </p>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onLogout}
                    className="h-8 w-8 flex-shrink-0 text-muted-foreground hover:text-destructive"
                    aria-label="Logout"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  Logout
                </TooltipContent>
              </Tooltip>
            </>
          )}
        </div>
      </div>
    </aside>
  )
}
